import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilLitigationWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CivilLitigationWizardDraft } from '../../../types/civilLitigationCaseRequest';
import { exceedsMax, sliceMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { CivilLitigationWizardActions } from './CivilLitigationWizardActions';
import { civilLitigationWizardPath, CivilLitigationWizardShell, FieldError, inpClass } from './CivilLitigationWizardShell';
import { isOtherCourtType } from './civilLitigationConstants';
import { isCaActionsAgainstStateSubCategory, requiresCourtType } from './civilLitigationHelpers';
import { useCivilLitigationLookups } from './useCivilLitigationLookups';
import { validateCivilLitigationStep } from './civilLitigationStepValidation';
import { useCaseCategorization } from './useCaseCategorization';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string; code?: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function CivilLitigationStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilLitigationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);
  const lookups = useCivilLitigationLookups(
    'step1',
    draft.provinceId,
    draft.districtId,
    draft.courtTypeId,
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.connectedAgdFileRef?.trim() && !draft.connectedAgdFileRefAvailable) {
      setDraft((d: CivilLitigationWizardDraft) => ({ ...d, connectedAgdFileRefAvailable: true }));
    }
  }, [draft.connectedAgdFileRef, draft.connectedAgdFileRefAvailable, setDraft]);


  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilLitigationStep(
      1,
      draft,
      cat.fileCategories,
      lookups.corporateInstitutes,
      cat.subCategories,
    );
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const isCaSubCategory = isCaActionsAgainstStateSubCategory(
    draft.subFileCategoryId,
    cat.subCategories,
    draft.subFileCategoryName,
  );
  const showCourtType = requiresCourtType(
    draft.hasActionInstituted,
    draft.subFileCategoryId,
    cat.subCategories,
    draft.subFileCategoryName,
  );
  const showCourtName = draft.hasActionInstituted === 'yes';
  const showCourtOther = showCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName);

  const next = () => {
    if (!validate()) return;
    nav(civilLitigationWizardPath(2, draft.draftRequestId));
  };

  return (
    <CivilLitigationWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="clit-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d: CivilLitigationWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="clit-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={toOpts(lookups.provinces)}
              onChange={(id) => {
                setDraft((d: CivilLitigationWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '',
                  courtId: '',
                  courtName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>{f.district}</label>
            <WizardSelect
              id="clit-district"
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={toOpts(lookups.districts)}
              onChange={(id) => {
                setDraft((d: CivilLitigationWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id),
                  courtId: '',
                  courtName: '' }));
              }}
            />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="clit-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: CivilLitigationWizardDraft) => ({ ...d, yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) =>
                setDraft((d: CivilLitigationWizardDraft) => ({ ...d, otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))
              }
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fg" id="clit-actionInstituted" style={{ marginBottom: 14 }}>
          <label>
            {f.hasActionInstituted} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="actionInstituted"
                checked={draft.hasActionInstituted === 'yes'}
                onChange={() => {
                  setDraft((d: CivilLitigationWizardDraft) => ({ ...d, hasActionInstituted: 'yes' }));
                  clearErr('hasActionInstituted');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio" style={isCaSubCategory ? { cursor: 'not-allowed', opacity: 0.5 } : undefined}>
              <input
                type="radio"
                name="actionInstituted"
                checked={draft.hasActionInstituted === 'no'}
                disabled={isCaSubCategory}
                onChange={() => {
                  setDraft((d: CivilLitigationWizardDraft) => {
                    const keepCourtType = isCaActionsAgainstStateSubCategory(
                      d.subFileCategoryId,
                      cat.subCategories,
                      d.subFileCategoryName,
                    );
                    return {
                      ...d,
                      hasActionInstituted: 'no',
                      courtId: '',
                      courtName: '',
                      ...(keepCourtType
                        ? {}
                        : {
                            courtTypeId: '',
                            courtTypeName: '',
                            courtTypeOther: '' }) };
                  });
                  clearErr('hasActionInstituted');
                  clearErr('courtTypeId');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.hasActionInstituted} />
        </div>

        {showCourtType ? (
          <div className={showCourtName ? 'ccr-fr c2' : 'ccr-fg'} style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>
                {f.courtType} <span className="req">*</span>
              </label>
              <WizardSelect
                id="clit-courtType"
                hasError={Boolean(errors.courtTypeId)}
                value={draft.courtTypeId}
                placeholder={f.courtType}
                options={toOpts(lookups.courtTypes)}
                onChange={(id) => {
                  const name = lookupName(lookups.courtTypes, id);
                  setDraft((d: CivilLitigationWizardDraft) => ({
                    ...d,
                    courtTypeId: id,
                    courtTypeName: name,
                    courtTypeOther: '',
                    courtId: '',
                    courtName: '' }));
                  clearErr('courtTypeId');
                }}
              />
              <FieldError message={errors.courtTypeId} />
            </div>
            {showCourtName ? (
              <div className="ccr-fg">
                <label>{f.courtName}</label>
                <WizardSelect
                  id="clit-courtName"
                  value={draft.courtId}
                  disabled={!draft.courtTypeId || lookups.courtsLoading}
                  placeholder={lookups.courtsLoading ? 'Loading courts…' : f.courtName}
                  options={toOpts(lookups.courts)}
                  onChange={(id) => {
                    setDraft((d: CivilLitigationWizardDraft) => ({
                      ...d,
                      courtId: id,
                      courtName: lookupName(lookups.courts, id) }));
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {showCourtOther ? (
          <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
            <label>{f.courtTypeOther}</label>
            <input
              id="clit-courtTypeOther"
              className={inpClass(Boolean(errors.courtTypeOther))}
              type="text"
              value={draft.courtTypeOther}
              onChange={(e) => {
                setDraft((d: CivilLitigationWizardDraft) => ({ ...d, courtTypeOther: maxLen('courtTypeOther', e.target.value, MAX_REF) }));
                clearErr('courtTypeOther');
              }}
              placeholder={f.specifyCourtType}
            />
            <FieldError message={errors.courtTypeOther} />
          </div>
        ) : null}

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.caseNumber} <span className="req">*</span>
            </label>
            <input
              id="clit-caseNumber"
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"
              value={draft.caseNumber}
              onChange={(e) => {
                setDraft((d: CivilLitigationWizardDraft) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
          <div className="ccr-fg" id="clit-connected">
            <label>
              {f.connectedMatters} <span className="req">*</span>
            </label>
            <div className="ccr-radio-row">
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'yes'}
                  onChange={() => {
                    setDraft((d: CivilLitigationWizardDraft) => ({ ...d, hasConnectedMatters: 'yes' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.yes}</span>
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'no'}
                  onChange={() => {
                    setDraft((d: CivilLitigationWizardDraft) => ({
                      ...d,
                      hasConnectedMatters: 'no',
                      connectedCaseNumber: '',
                      connectedAgdFileRefAvailable: false,
                      connectedAgdFileRef: '' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.no}</span>
              </label>
            </div>
            <FieldError message={errors.hasConnectedMatters} />
          </div>
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <>
            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <label>
                  {f.connectedCaseNumber} <span className="req">*</span>
                </label>
              </div>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ marginBottom: 0 }}>
                    {f.connectedAgdFileRef} <span className="req">*</span>
                  </label>
                  <input
                    type="checkbox"
                    style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer', flexShrink: 0 }}
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d: CivilLitigationWizardDraft) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                      }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>Available</span>
                </div>
              </div>
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg">
                <input
                  id="clit-connectedCase"
                  className={inpClass(Boolean(errors.connectedCaseNumber))}
                  type="text"
                  maxLength={MAX_REF}
                  value={draft.connectedCaseNumber}
                  onChange={(e) => {
                    setDraft((d: CivilLitigationWizardDraft) => ({ ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedCaseNumber');
                  }}
                  placeholder={f.enterConnectedCaseNumber}
                />
                <FieldError message={errors.connectedCaseNumber} />
              </div>
              <div className="ccr-fg">
                <input
                  id="clit-connectedAgd"
                  className={inpClass(Boolean(errors.connectedAgdFileRef))}
                  type="text"
                  maxLength={MAX_REF}
                  disabled={!draft.connectedAgdFileRefAvailable}
                  style={!draft.connectedAgdFileRefAvailable ? { cursor: 'default', pointerEvents: 'none' } : undefined}
                  value={draft.connectedAgdFileRef}
                  onChange={(e) => {
                    setDraft((d: CivilLitigationWizardDraft) => ({ ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedAgdFileRef');
                  }}
                  placeholder={f.enterConnectedAgdFileRef}
                />
                <div className="ccr-slbl">
                  If you are unaware of the relevant Connected AGD File Reference Number details, please leave the
                  'Available' checkbox unchecked.
                </div>
                <FieldError message={errors.connectedAgdFileRef} />
              </div>
            </div>
          </>
        ) : null}
      </SectionCard>

      <CivilLitigationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </CivilLitigationWizardShell>
  );
}
